import axios from "axios";
import React from "react";

import { useMsal } from "@azure/msal-react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useMutation } from "@tanstack/react-query";

import {
  Loader,
  PyButton,
  TOASTER_TYPE,
  ToastMessage,
} from "../../../../../../components";
import { usePayLinkCart } from "../../../../context/PayLinkCartContext";
import { usePayLinkUser } from "../../../../context/PaylinkUserContext";
import { createPayLinkCardToken } from "../../../../services/api";

interface IProps {
  refetchCardToken: () => void;
  openMethodAccordion: () => void;
  cardTokenLoading: boolean;
  setupIntentData: any;
  stripeCustomer: any;
}

export const AddPayLinkStripePaymentMethod: React.FC<IProps> = ({
  refetchCardToken,
  openMethodAccordion,
  cardTokenLoading,
  setupIntentData,
  stripeCustomer,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const { accounts } = useMsal();
  const { user, shippingAddress } = usePayLinkUser();
  const { storeProfile } = usePayLinkCart();

  const onCreateCardToken = useMutation({
    mutationFn: createPayLinkCardToken,
    onSuccess: (res: any) => {
      openMethodAccordion();
      refetchCardToken();
      ToastMessage(TOASTER_TYPE.SUCCESS, "Card added successfully");
    },
    onError: (error: unknown) => {
      if (axios.isAxiosError(error)) {
        ToastMessage(
          TOASTER_TYPE.ERROR,
          error.response?.data?.message || "Failed to save card"
        );
      } else {
        ToastMessage(TOASTER_TYPE.ERROR, "Failed to save card");
      }
    },
  });

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      ToastMessage(TOASTER_TYPE.ERROR, "Stripe not initialized");
      return;
    }

    if (!stripeCustomer?.id) {
      ToastMessage(TOASTER_TYPE.ERROR, "Stripe customer not available");
      return;
    }

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: window.location.href,
      },
    });

    if (error) {
      ToastMessage(TOASTER_TYPE.ERROR, error.message || "Payment failed");
      console.error(error);
      return;
    }

    if (setupIntent && setupIntent.status === "succeeded") {
      ToastMessage(TOASTER_TYPE.SUCCESS, "Card verified successfully");
      console.log("setupIntent", setupIntent);
      handleCreateCardToken(setupIntent, stripeCustomer.id);
    }
  };

  const handleCreateCardToken = (setupIntent: any, customerId: string) => {
    if (!user) {
      ToastMessage(TOASTER_TYPE.ERROR, "User not found");
      return;
    }

    const body = {
      merchantUniqueReferenceNumber: storeProfile?.customerUniqueReferenceNumber,
      customerUniqueReferenceNumber: storeProfile?.customerUniqueReferenceNumber,
      paymentGatewayReference:
        setupIntent?.payment_method || setupIntent?.paymentMethodId,
      customerEmailAddress: user.email,
      walletUniqueReferenceNumber: accounts[0]?.idTokenClaims?.sub || user.walletUniqueReferenceNumber,
      consumerUniqueReferenceNumber: user.consumerUniqueReferenceNumber,
      newCustomer: false,
      gatewayCustomerId: customerId,
      verification: {
        verificationRequired: false,
        firstName: user?.firstName || shippingAddress?.firstName || "",
        lastName: user?.lastName || shippingAddress?.lastName || "",
        cardType: "debit",
        brand: "unknown",
      },
    };

    console.log(body);

    onCreateCardToken.mutate(body as any);
  };

  return (
    <>
      {(onCreateCardToken.isLoading || cardTokenLoading) && (
        <Loader isFullPage />
      )}

      <div className="flex flex-col gap-4 w-full rounded-2xl">
        <div className="w-full">
          <PaymentElement
            options={{
              layout: "tabs",
            }}
          />
        </div>

        <div className="flex gap-4 items-center mt-4">
          <PyButton variant="SecondaryOutline" extraClass="!w-[104px]" click={openMethodAccordion}>
            Cancel
          </PyButton>
          <PyButton
            click={handleSubmit}
            type="button"
            extraClass="!w-[104px]"
            isDisabled={!stripe || !elements || !stripeCustomer?.id}
            loading={onCreateCardToken.isLoading}>
            Add
          </PyButton>
        </div>
      </div>
    </>
  );
};
